import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, CircularProgress, Alert, Box, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip, Grid } from '@mui/material';
import { Visibility, Refresh, DriveEta, Person, Business, Edit, Delete, Add, CardMembership } from '@mui/icons-material';
import { customModal } from './CustomModal';

const VehicleList = () => {
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [openView, setOpenView] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [owners, setOwners] = useState([]);
    const [drivers, setDrivers] = useState([]);

    const [formData, setFormData] = useState({
        vehicleNumber: '',
        type: 'Lorry',
        model: '',
        capacity: '',
        status: 'active',
        ownerId: '',
        driverId: ''
    });
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        setError('');
        try {
            const [vRes, oRes, dRes] = await Promise.all([
                axios.get('/admin/all-vehicles'),
                axios.get('/admin/owners'),
                axios.get('/admin/drivers')
            ]);
            setVehicles(vRes.data);
            setOwners(oRes.data);
            setDrivers(dRes.data);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to fetch data");
        } finally {
            setLoading(false);
        }
    };

    const handleView = (vehicle) => {
        setSelectedVehicle(vehicle);
        setOpenView(true);
    };

    const handleAddClick = () => {
        setIsEditing(false);
        setFormData({
            vehicleNumber: '',
            type: 'Lorry',
            model: '',
            capacity: '',
            status: 'active',
            ownerId: '',
            driverId: ''
        });
        setOpenEdit(true);
    };

    const handleEditClick = (vehicle) => {
        setIsEditing(true);
        setSelectedVehicle(vehicle);
        setFormData({
            vehicleNumber: vehicle.vehicleNumber || '',
            type: vehicle.type || 'Lorry',
            model: vehicle.model || '',
            capacity: vehicle.capacity || '',
            status: vehicle.status || 'active',
            ownerId: vehicle.ownerId?._id || '',
            driverId: vehicle.driverId?._id || ''
        });
        setOpenEdit(true);
    };

    const handleDelete = async (id) => {
        const confirmed = await customModal.confirm("Delete Vehicle", "Are you sure you want to delete this vehicle?", { type: 'error', confirmText: 'Delete' });
        if (confirmed) {
            try {
                await axios.delete(`/admin/vehicles/${id}`);
                fetchData();
            } catch (err) {
                setError("Failed to delete vehicle");
            }
        }
    };

    const handleSave = async () => {
        try {
            if (isEditing) {
                await axios.put(`/api/admin/vehicles/${selectedVehicle._id}`, formData);
            } else {
                await axios.post('/api/admin/vehicles', formData);
            }
            setOpenEdit(false);
            fetchData();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to save vehicle");
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

    return (
        <div>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4">All Vehicles</Typography>
                <Box>
                    <Button variant="contained" color="success" startIcon={<Add />} onClick={handleAddClick} sx={{ mr: 1 }}>
                        Add Vehicle
                    </Button>
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchData} color="primary"><Refresh /></IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Number</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Owner</TableCell>
                            <TableCell>Driver</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {vehicles.map((v) => (
                            <TableRow key={v._id}>
                                <TableCell>{v.vehicleNumber}</TableCell>
                                <TableCell>{v.type}</TableCell>
                                <TableCell>{v.ownerId?.businessName || v.ownerId?.name || '—'}</TableCell>
                                <TableCell>{v.driverId?.name || '—'}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={v.status}
                                        color={v.status === 'active' ? 'success' : v.status === 'pending' ? 'warning' : 'error'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <IconButton size="small" color="info" onClick={() => handleView(v)}><Visibility /></IconButton>
                                    <IconButton size="small" color="primary" onClick={() => handleEditClick(v)}><Edit /></IconButton>
                                    <IconButton size="small" color="error" onClick={() => handleDelete(v._id)}><Delete /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* View Dialog */}
            <Dialog open={openView} onClose={() => setOpenView(false)} maxWidth="md" fullWidth>
                <DialogTitle>Vehicle Details</DialogTitle>
                <DialogContent dividers>
                    {selectedVehicle && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                    <Typography variant="h6" gutterBottom><DriveEta sx={{ mr: 1, verticalAlign: 'middle' }} /> Vehicle Info</Typography>
                                    <Typography>Number: {selectedVehicle.vehicleNumber}</Typography>
                                    <Typography>Type: {selectedVehicle.type}</Typography>
                                    <Typography>Model: {selectedVehicle.model}</Typography>
                                    <Typography>Capacity: {selectedVehicle.capacity}</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                                    <Typography variant="h6" gutterBottom><CardMembership sx={{ mr: 1, verticalAlign: 'middle' }} /> Membership</Typography>
                                    <Typography>Status: {selectedVehicle.isVerified ? 'Verified' : 'Unverified'}</Typography>
                                    <Typography>Expiry: {selectedVehicle.rateExpiration ? new Date(selectedVehicle.rateExpiration).toLocaleDateString() : 'No Plan'}</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 2, bgcolor: '#f1f8e9', borderRadius: 1, mt: 1 }}>
                                    <Typography variant="h6" gutterBottom><Business sx={{ mr: 1, verticalAlign: 'middle' }} /> Owner Details</Typography>
                                    <Typography>Name: {selectedVehicle.ownerId?.name || '—'}</Typography>
                                    <Typography>Business: {selectedVehicle.ownerId?.businessName || '—'}</Typography>
                                    <Typography>Phone: {selectedVehicle.ownerId?.phoneNumber || '—'}</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 2, bgcolor: '#fffde7', borderRadius: 1, mt: 1 }}>
                                    <Typography variant="h6" gutterBottom><Person sx={{ mr: 1, verticalAlign: 'middle' }} /> Driver Details</Typography>
                                    <Typography>Name: {selectedVehicle.driverId?.name || '—'}</Typography>
                                    <Typography>Phone: {selectedVehicle.driverId?.phoneNumber || '—'}</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions><Button onClick={() => setOpenView(false)}>Close</Button></DialogActions>
            </Dialog>

            {/* Edit/Add Dialog */}
            <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{isEditing ? 'Edit Vehicle' : 'Add Vehicle'}</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Vehicle Number" fullWidth value={formData.vehicleNumber} onChange={(e) => setFormData({ ...formData, vehicleNumber: e.target.value })} />
                        <TextField label="Type" select fullWidth SelectProps={{ native: true }} value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                            <option value="Lorry">Lorry</option>
                            <option value="Tractor">Tractor</option>
                        </TextField>
                        <TextField label="Model" fullWidth value={formData.model} onChange={(e) => setFormData({ ...formData, model: e.target.value })} />
                        <TextField label="Capacity" fullWidth value={formData.capacity} onChange={(e) => setFormData({ ...formData, capacity: e.target.value })} />
                        <TextField label="Owner" select fullWidth SelectProps={{ native: true }} value={formData.ownerId} onChange={(e) => setFormData({ ...formData, ownerId: e.target.value })}>
                            <option value="">Select Owner</option>
                            {owners.map(o => <option key={o._id} value={o._id}>{o.businessName || o.name}</option>)}
                        </TextField>
                        <TextField label="Driver" select fullWidth SelectProps={{ native: true }} value={formData.driverId} onChange={(e) => setFormData({ ...formData, driverId: e.target.value })}>
                            <option value="">Select Driver (Optional)</option>
                            {drivers.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                        </TextField>
                        <TextField label="Status" select fullWidth SelectProps={{ native: true }} value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                            <option value="active">Active</option>
                            <option value="pending">Pending</option>
                            <option value="inactive">Inactive</option>
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleSave}>Save</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default VehicleList;
